import forEach from 'lodash/forEach';

interface Sanitizer {
  sanitize: (value: string) => string;
}

export interface QueryObject {
  [key: string]: string | number | boolean | undefined;
  page?: number;
  border?: boolean;
}

export interface ParsedQueryObject {
  [key: string]: string | boolean | undefined;
}

export interface StringifyOptions {
  includePageParam?: boolean;
}

export interface AddOrUpdateUrlParamOptions {
  url?: string;
  name: string;
  value: string | number;
}

export const queryUtilsSanitize = (obj: QueryObject = {}): Promise<Record<string, string>> => {
  return import(/* webpackChunkName: "sanitizer" */ 'sanitizer').then(
    ({ default: sanitizer }: { default: Sanitizer }) => {
      const newQueryObj: Record<string, string> = {};

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key]) {
          const queryValue = obj[key];

          newQueryObj[encodeURIComponent(key)] = encodeURIComponent(sanitizer.sanitize(String(queryValue)));
        }
      }

      return newQueryObj;
    },
  );
};

const queryUtils = {
  isEncoded(uri: string): boolean {
    const uriStr = uri || '';

    try {
      decodeURIComponent(uriStr);
      return uriStr !== decodeURIComponent(uriStr);
    } catch (e) {
      if (e instanceof URIError) {
        return false;
      }
      throw e;
    }
  },
  fullyDecodeURI(uri: string): string {
    let decodedUri = uri;
    while (queryUtils.isEncoded(decodedUri)) {
      try {
        decodedUri = decodeURIComponent(decodedUri);
      } catch (e) {
        if (e instanceof URIError) {
          decodedUri = encodeURI(decodedUri);
        }
      }
    }
    return decodedUri;
  },
  stringify(obj: QueryObject = {}, opts: StringifyOptions = {}): string {
    const str: Array<string> = [];
    const mutableObj = { ...obj };

    if (opts.includePageParam) {
      mutableObj.page = mutableObj.page ?? 1;
    }

    forEach(mutableObj, (v, k) => {
      if (v || v === false) {
        const decodedKey = queryUtils.fullyDecodeURI(k);
        const decodedValue = queryUtils.fullyDecodeURI(String(v));
        str.push(encodeURIComponent(decodedKey) + '=' + encodeURIComponent(decodedValue));
      }
    });

    if (str.length > 0) {
      return '?' + str.sort().join('&');
    } else {
      return '';
    }
  },
  parse(string = ''): ParsedQueryObject {
    if (typeof string !== 'string' || string === '') {
      return {};
    }
    let queryString = string;
    if (queryString.charAt(0) === '?') {
      queryString = queryString.substring(1);
    }
    const queryObj: ParsedQueryObject = {};
    const pieces = queryString.split('&');
    pieces.forEach((piece) => {
      const splitPiece = piece.split('=');
      const name = splitPiece[0];
      let value: string | boolean | undefined = splitPiece[1];
      if (value === 'true') {
        value = true;
      }
      if (value === 'false') {
        value = false;
      }
      queryObj[name] = value;
    });

    return queryObj;
  },
  removeQueryString(url = ''): string {
    return url.split('?')[0];
  },
  removeNonMapQueries(url = ''): string {
    const pathquery = url.split('?');
    const mapQueries: Array<string> = [];
    const queries = pathquery[1] ? pathquery[1].split('&') : [];
    for (let i = 0; i < queries.length; i++) {
      const query = queries[i].toLowerCase();
      if (query.includes('border') || query.includes('lat') || query.includes('lon') || query.includes('z')) {
        mapQueries.push(query);
      }
    }

    if (mapQueries.length > 0) {
      return pathquery[0] + '?' + mapQueries.join('&');
    } else {
      return pathquery[0];
    }
  },
  combineMapQueryParams(urlWithMap: string, url: string): string {
    if (!urlWithMap) {
      return url;
    }
    const destinationPath = queryUtils.removeQueryString(url);
    const mapQuery = urlWithMap.split('?')[1] || '';
    const filterQuery = url.split('?')[1] || '';
    const combinedQuery = [mapQuery, filterQuery].filter(Boolean).join('&');

    return destinationPath + (combinedQuery ? '?' + combinedQuery : '');
  },
  addOrUpdateUrlParam({ url, name, value }: AddOrUpdateUrlParamOptions): string {
    // adapted from https://stackoverflow.com/a/6899293/7669090
    const href = url || window.location.href;
    let result: string;
    let regex = new RegExp('[&\\?]' + name + '=');
    if (regex.test(href)) {
      regex = new RegExp('([&\\?])' + name + '=\\d+');
      result = href.replace(regex, '$1' + name + '=' + value);
    } else {
      if (href.indexOf('?') > -1) {
        result = href + '&' + name + '=' + value;
      } else {
        result = href + '?' + name + '=' + value;
      }
    }
    return result;
  },
  isMapBorderActive(queryObj: QueryObject = {}): boolean {
    /**
     * If the map border is INACTIVE, the "border" query param is explicitly
     * set to `false`. The "border" query param being sent to `null` or `undefined`
     * have no effect on the map border being present or not.
     */
    const mapHasBorder = queryObj.border !== false;

    return mapHasBorder;
  },
};

export default queryUtils;
